'use client';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useConsentStatus, getApiUrl } from '@practium/features/api';
import type { ConsentStatus } from '@practium/features/api';
import { authStorage } from '../../storage/authStorage';
import { authLog } from '../../logging/auth.logger';
import { User } from '@practium/shared';
import { ConsentGateModal } from './ConsentGateModal';

const TOKEN_KEY = 'practium_token';

interface LoginConsentGateProps {
	token: string;
	rememberMe?: boolean;
	onComplete: () => void;
}

export const LoginConsentGate = ({
	token,
	rememberMe = true,
	onComplete,
}: LoginConsentGateProps) => {
	const [initialStatus, setInitialStatus] = useState<ConsentStatus | null>(null);
	const completedRef = useRef(false);
	const queryClient = useQueryClient();

	useEffect(() => {
		localStorage.setItem(TOKEN_KEY, token);

		axios
			.get<ConsentStatus>(`${getApiUrl()}consent/status`, {
				headers: { Authorization: `bearer ${token}` },
			})
			.then(({ data }) => {
				queryClient.setQueryData<ConsentStatus>(['consent', 'status'], data);
				setInitialStatus(data);
			})
			.catch(() => {
				const fallback: ConsentStatus = {
					allGranted: false,
					accepted: false,
					requiredConsents: [],
					totalActiveConsents: 0,
					totalPendingConsents: 0,
				};
				queryClient.setQueryData<ConsentStatus>(['consent', 'status'], fallback);
				setInitialStatus(fallback);
			});
	}, [token, queryClient]);

	const { data: cachedStatus } = useConsentStatus(false);
	const effectiveStatus = cachedStatus ?? initialStatus;

	useEffect(() => {
		if (!effectiveStatus?.allGranted || completedRef.current) return;
		completedRef.current = true;

		axios
			.get<User>(`${getApiUrl()}user/get`, {
				headers: { Authorization: `bearer ${token}` },
			})
			.then(({ data: user }) => {
				authStorage.saveSession(user, token, rememberMe);
				authLog.logLogin(user._id, user.email, rememberMe);
				onComplete();
			})
			.catch(() => {
				completedRef.current = false;
			});
	}, [effectiveStatus?.allGranted, token, rememberMe, onComplete]);

	if (!initialStatus) return null;
	if (effectiveStatus?.allGranted) return null;

	return <ConsentGateModal consentStatus={effectiveStatus as ConsentStatus} />;
};
